import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Flex, Progress, Select, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter, SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { MethodTag, NoWms, TaskStatusTag, useWmsWarehouse, ZoneTag, type Task } from "./common";
import { MoveTaskModal } from "./MoveTask";
import { usePoll } from "@/components/tabs/WorkTabs";

const STATUS_CHOICES = ["active", "done", "cancelled", "all"] as const;
const TASK_STATUSES = ["open", "in_progress", "done", "cancelled"] as const;
const STATUS_PARAM: Record<(typeof STATUS_CHOICES)[number], string | undefined> = { active: "open,in_progress", done: "done", cancelled: "cancelled", all: undefined };
const SOURCES = ["receiving", "inbound", "packing", "outbound", "transfer", "move", "vendor_return"] as const;
const METHODS = ["single", "batch", "cluster", "transfer"] as const;

/** Gudang > Putaway (and Picking): the task list of one kind. */
export function Tasks({ kind }: { kind: Task["kind"] }) {
  const { t, tDynamic, formatDateTime } = useI18n();
  const { me, can } = useAuth();
  const [moving, setMoving] = useState(false);
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { current, loading } = useWmsWarehouse();
  const status = useStatusFilter(STATUS_CHOICES, "active");
  const search = useSearchFilter();
  const range = useDateRange();
  const page = useListPage();
  const tz = me?.workspace?.timezone;

  const statusParam = STATUS_PARAM[status.value];
  const filters = { warehouseId: current?.id ?? "", kind, ...(statusParam ? { status: statusParam } : {}), ...search.query, ...range.query };
  const list = useQuery({
    queryKey: ["tasks", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/tasks", { params: { query: { ...filters, ...page.query } } })),
    enabled: !!current,
    refetchInterval: usePoll(15_000),
    placeholderData: keepPreviousData,
  });
  const manage = can("warehouse.manage");
  const workers = useQuery({
    queryKey: ["workers", current?.id],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/workers", { params: { path: { warehouseID: current!.id } } })),
    enabled: !!current && manage,
  });
  // "Ambil task berikutnya": one's own open task, or the oldest free one.
  const takeNext = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/tasks/next", { body: { warehouseId: current!.id, kind } })),
    onSuccess: (x) => {
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      navigate(`/app/warehouse/tasks/${x.id}`);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const assign = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string | null }) =>
      unwrap(api.POST("/v1/tasks/{taskID}/assign", { params: { path: { taskID: id } }, body: { userId } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["workers"] });
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const fromReceiving = useMutation({
    mutationFn: (from: "receiving" | "packing" | "outbound") =>
      unwrap(api.POST("/v1/warehouses/{warehouseID}/putaway", { params: { path: { warehouseID: current!.id }, query: { from } } })),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      navigate(`/app/warehouse/tasks/${r.taskId}`);
    },
    onError: (err) => void message.error(errorText(err)),
  });

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t(`wms.task.title.${kind}`)}
        </Typography.Title>
        {kind === "picking" && (
          <Button type="primary" loading={takeNext.isPending} onClick={() => takeNext.mutate()}>
            {t("wms.task.takeNext")}
          </Button>
        )}
        {kind === "putaway" && (
          <Space wrap>
            <Button loading={takeNext.isPending} onClick={() => takeNext.mutate()}>
              {t("wms.task.takeNext")}
            </Button>
            {can("warehouse.manage") && <Button onClick={() => setMoving(true)}>{t("wms.move.title")}</Button>}
            <Button loading={fromReceiving.isPending && fromReceiving.variables === "packing"} onClick={() => fromReceiving.mutate("packing")}>
              {t("wms.task.fromPacking")}
            </Button>
            <Button loading={fromReceiving.isPending && fromReceiving.variables === "outbound"} onClick={() => fromReceiving.mutate("outbound")}>
              {t("wms.task.fromOutbound")}
            </Button>
            <Button type="primary" loading={fromReceiving.isPending && fromReceiving.variables === "receiving"} onClick={() => fromReceiving.mutate("receiving")}>
              {t("wms.task.fromReceiving")}
            </Button>
          </Space>
        )}
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("list.searchNumber")} />
        <StatusFilter
          filter={status}
          options={[
            { value: "active", label: t("list.openOnly") },
            { value: "done", label: t("wms.task.status.done") },
            { value: "cancelled", label: t("wms.task.status.cancelled") },
            { value: "all", label: t("list.allStatuses") },
          ]}
        />
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/tasks/export"
          query={filters}
          filename={t(`wms.task.title.${kind}`)}
          columns={[
            { key: "number", label: t("wms.task.number") },
            { key: "warehouseName", label: t("orders.warehouse") },
            { key: "status", label: t("common.status"), values: Object.fromEntries(TASK_STATUSES.map((v) => [v, t(`wms.task.status.${v}`)])) },
            ...(kind === "picking"
              ? [{ key: "method", label: t("wms.pool.method"), values: Object.fromEntries(METHODS.map((v) => [v, t(`wms.method.${v}`)])) }]
              : [{ key: "sourceType", label: t("wms.task.source"), values: Object.fromEntries(SOURCES.map((v) => [v, t(`wms.task.source.${v}`)])) }]),
            { key: "doneUnits", label: t("wms.task.doneUnits"), kind: "number" as const },
            { key: "units", label: t("wms.task.units"), kind: "number" as const },
            { key: "assignedName", label: t("wms.task.assigned") },
            { key: "createdAt", label: t("list.createdAt"), kind: "datetime" as const },
            { key: "doneAt", label: t("list.completedAt"), kind: "datetime" as const },
          ]}
        />
      </Flex>
      <Table<Task>
        rowKey="id"
        loading={list.isFetching && !list.data}
        dataSource={list.data?.items ?? []}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("wms.task.empty") }}
        onRow={(x) => ({ onClick: () => navigate(`/app/warehouse/tasks/${x.id}`), style: { cursor: "pointer" } })}
        columns={[
          {
            title: t("wms.task.number"),
            dataIndex: "number",
            width: 190,
            render: (v: string, x: Task) => (
              <Space size={6}>
                <Typography.Text strong className="z-num">{v}</Typography.Text>
                <ZoneTag zone={x.zone} />
              </Space>
            ),
          },
          { title: t("common.status"), dataIndex: "status", width: 140, render: (s: Task["status"]) => <TaskStatusTag status={s} /> },
          ...(kind === "picking" ? [{ title: t("wms.pool.method"), dataIndex: "method", width: 120, render: (m: string) => <MethodTag method={m} /> }] : []),
          ...(kind === "putaway"
            ? [{ title: t("wms.task.source"), dataIndex: "sourceType", width: 160, render: (v: string) => <Tag>{tDynamic(`wms.task.source.${v}`, "wms.task.source.receiving")}</Tag> }]
            : []),
          {
            title: t("wms.task.progress"),
            render: (_, x) => <Progress percent={Math.round((x.doneUnits / Math.max(x.units, 1)) * 100)} size="small" format={() => `${x.doneUnits}/${x.units}`} />,
          },
          {
            title: t("wms.task.assigned"),
            width: 210,
            render: (_, x) => {
              const open = x.status === "open" || x.status === "in_progress";
              const mine = x.assignedTo != null && x.assignedTo === me?.user.id;
              if (manage && open) {
                // A supervisor gives the task to someone, or frees it.
                return (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Select<string>
                      size="small"
                      style={{ width: "100%" }}
                      value={x.assignedTo ?? ""}
                      loading={assign.isPending && assign.variables?.id === x.id}
                      onChange={(v) => assign.mutate({ id: x.id, userId: v || null })}
                      options={[
                        // Once something was picked it is handed over to someone, not freed.
                        { value: "", label: t("wms.task.unassigned"), disabled: kind === "picking" && x.doneUnits > 0 },
                        ...(workers.data?.items ?? []).map((w) => ({ value: w.userId, label: `${w.name} (${w.activeTasks})` })),
                        // Someone who no longer works here still shows by name.
                        ...(x.assignedTo && !(workers.data?.items ?? []).some((w) => w.userId === x.assignedTo) ? [{ value: x.assignedTo, label: x.assignedName }] : []),
                      ]}
                    />
                  </div>
                );
              }
              return (
                <Space size={4}>
                  <span>{x.assignedName || "—"}</span>
                  {mine && <Tag color="processing">{t("wms.task.mine")}</Tag>}
                </Space>
              );
            },
          },
          { title: t("orders.orderedAt"), dataIndex: "createdAt", width: 170, render: (v: string) => formatDateTime(v, tz) },
        ]}
      />
      {current && <MoveTaskModal open={moving} warehouseId={current.id} onClose={() => setMoving(false)} />}
    </>
  );
}
